import { CORE_PAGES } from '@core-ui/components';
import { TopBanner } from '@moniebook-web/components/atoms/top-banner';
import type { TopBannerProps } from '@moniebook-web/components/atoms/top-banner/types';
import { SYSTEM_BANNER_ID, useUserDateFormatter } from '@moniebook-web/utils';
import { AnimatePresence, motion } from 'framer-motion';

import { type BannerInfo, BannerType } from '../types';
import { useSystemBanner } from '../use-system-banner';

function getTopBannerVariant(type?: BannerType): TopBannerProps['variant'] {
    switch (type) {
        case BannerType.INFO:
            return 'information';
        case BannerType.SUCCESS:
            return 'positive';
        case BannerType.WARN:
            return 'warning';
        case BannerType.ERROR:
            return 'negative';
        default:
            return undefined;
    }
}

const EXPIRED_BANNER_ID = 'subscription-expired';
const EXPIRED_BANNER_COPY = {
    type: BannerType.ERROR,
    message:
        'Your subscription has expired, Reactivate to continue to have full access to this page.',
    label: 'Resubscribe Now',
    link: CORE_PAGES.SETTINGS.SELECT_PLAN,
};

const CANCELLED_BANNER_ID = 'subscription-cancelled-active';

function getCancelledBannerCopy(
    banner: BannerInfo,
    formatDate: (date: string, options: { dayFormat: string }) => string,
) {
    const formattedEndDate = banner.endDate
        ? formatDate(banner.endDate, { dayFormat: 'D MMMM YYYY' })
        : '';

    return {
        type: BannerType.ERROR,
        message: `Your subscription has been cancelled and will end on ${formattedEndDate}. Reactivate to continue using Moniebook.`,
        label: 'Reactivate now',
        link: banner.link,
    };
}

function getBannerCopy(
    banner: BannerInfo | undefined,
    formatDate: (date: string, options: { dayFormat: string }) => string,
) {
    if (banner?.id === EXPIRED_BANNER_ID) {
        return EXPIRED_BANNER_COPY;
    }

    if (banner?.id === CANCELLED_BANNER_ID) {
        return getCancelledBannerCopy(banner, formatDate);
    }

    return {
        type: banner?.type,
        message: banner?.message,
        label: banner?.label,
        link: banner?.link,
    };
}

export function SystemBanner() {
    const { banner, freeTrialMessage } = useSystemBanner();
    const { formatDate } = useUserDateFormatter();
    const { type, message, label, link } = getBannerCopy(banner, formatDate);

    return (
        <AnimatePresence>
            {freeTrialMessage && (
                <TopBanner
                    message={freeTrialMessage}
                    linkText='Pay now'
                    linkHref={CORE_PAGES.SETTINGS.COMPLETE_PAYMENT}
                    key='free-trial'
                />
            )}
            {!freeTrialMessage && banner && !banner.completed && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{
                        opacity: 1,
                        height: 'auto',
                        transition: {
                            height: {
                                duration: 0.2,
                            },
                            opacity: {
                                duration: 0.2,
                                delay: 0.1,
                            },
                        },
                    }}
                    key='banner'
                    exit={{
                        opacity: 0,
                        height: 0,
                        transition: {
                            height: {
                                duration: 0.2,
                            },
                            opacity: {
                                duration: 0,
                            },
                        },
                    }}
                    role='alert'
                    id={SYSTEM_BANNER_ID}
                    className='z-410 [&+div]:rounded-t-16'>
                    <TopBanner
                        bannerType='solid'
                        variant={getTopBannerVariant(type)}
                        message={message}
                        linkHref={link}
                        linkText={label}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}
